import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import { toWgs84 } from '@turf/projection'
import $ from 'jquery'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import OLFeature from 'ol/Feature'
import OLGeomLineString from 'ol/geom/LineString'
import OLGeomPoint from 'ol/geom/Point'
import OLGeomPolygon from 'ol/geom/Polygon'
import Draw from 'ol/interaction/Draw'
import Modify from 'ol/interaction/Modify'
import Select from 'ol/interaction/Select'
import Snap from 'ol/interaction/Snap'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { HistoryUpdate } from 'api/history'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import GeoJSONIO from 'data/geojsonio'
import PoziMap from 'map/map'
import { showSnackbar as showSnackbarAction } from 'utils/snackbar/actions'
import { SNACKBAR_WARNING } from 'utils/snackbar/PositionedSnackbar'
import { zip } from 'utils/zip'

import { LINE_STRING, POINT, POINT_LABEL, POLYGON, REMOVE, SKETCH_POINT } from './LayerPanelEdit'

const SILENT_UPDATE = true

const styles = theme => ({
  root: {}
})

class DrawAnnotations extends React.Component {
  draw = null

  snap = null

  modify = null

  olLayer = null

  addfeatureListenerFn = null

  changeListenerFn = null

  bindEscape = null

  constructor (props) {
    super(props)
    this.state = {
      colour: props.colour || '#0000ff'
    }
  }

  componentWillUnmount () {
    this.removeInteractions()
  }

  componentDidUpdate (prevProps) {
    const { layer, type } = this.props

    if (isNil(layer) || isNil(type) || type === '') {
      this.removeInteractions()
      return null
    }

    this.addInteractions()

    // Only show info message when we change the drawing type
    if (prevProps.type !== type) {
      this.showInfoMessage()
    }
  }

  clearAnnotations () {
    console.debug('DrawAnnotations - clear', this.olLayer?.get('titleid'))
    PoziMap().fadeOutLayer(this.olLayer)
  }

  bindEscapeKey (e) {
    if (e.key === 'Escape') {
      const action = 'Annotation escape key used'
      PoziUtils.gtag({
        category: 'useraction',
        status: 'success',
        identifier: action,
        action
      })
      this.removeInteractions()
      // return false // Do not propagate the click event.
    }
  }

  removeInteractions () {
    const { map } = this.props
    const { draw, snap, modify, remove } = this
    if (!isNil(draw)) {
      map.removeInteraction(draw)
    }
    if (!isNil(snap)) {
      map.removeInteraction(snap)
    }
    if (!isNil(modify)) {
      map.removeInteraction(modify)
    }
    if (!isNil(remove)) {
      map.removeInteraction(remove)
    }
    if (!isNil(this.bindEscape)) {
      this.bindEscape = null
      $(document).unbind('keyup', this.bindEscapeKey.bind(this))
    }
    PoziMap().enableDefaultMapClickInteration()
  }

  addInteractions () {
    const { map, layer, type } = this.props

    const action = 'Annotation type ' + type + ' clicked.'
    PoziUtils.gtag({
      category: 'useraction',
      status: 'success',
      identifier: action,
      action
    })

    this.removeInteractions()

    PoziMap().disableDefaultMapClickInteration()
    this.olLayer = PoziUtils.getOpenLayersObjectFromTitleID(layer.titleid)
    const source = this.olLayer.getSource()

    if (isNil(this.bindEscape)) {
      this.bindEscape = true
      $(document).bind('keyup', this.bindEscapeKey.bind(this))
    }

    if (isNil(this.addfeatureListenerFn)) {
      this.addfeatureListenerFn = this.onNewFeature.bind(this)
      source.on('addfeature', this.addfeatureListenerFn)
    }
    if (isNil(this.changeListenerFn)) {
      this.changeListenerFn = this.onSourceChange.bind(this)
      source.on('change', this.changeListenerFn)
    }

    // Special interaction for removing features
    if (type === REMOVE) {
      this.remove = new Select({ source })
      this.remove.on('select', this.onSelect)

      map.addInteraction(this.remove)

      return
    }

    const drawStyle = this.getDrawStyle({ type })

    // Draw interaction
    if (type === POINT || type === POINT_LABEL) {
      this.draw = new Draw({ source, type: POINT, style: drawStyle })
    } else {
      // Create special style for line/polygon drawing in order to show the sketch point
      const sketchPointStyle = this.getDrawStyle({ type: SKETCH_POINT })
      const styleFunction = (feature, resolution) => {
        const geomType = feature.getGeometry().getType()
        return geomType === POINT ? sketchPointStyle : drawStyle
      }
      this.draw = new Draw({ source, type, style: styleFunction })
    }

    this.modify = new Modify({ source })
    this.snap = new Snap({ source })

    map.addInteraction(this.modify)
    map.addInteraction(this.draw)
    map.addInteraction(this.snap)
  }

  showInfoMessage () {
    const { type, showSnackbar } = this.props

    switch (type) {
      case POINT_LABEL:
        showSnackbar({ message: 'Enter text then click on the map.' })
        break
      case POINT:
        showSnackbar({ message: 'Click on the map to place a point.' })
        break
      case LINE_STRING:
      case POLYGON:
        showSnackbar({ message: 'Click on the map to start drawing. Double click to end. (Hold shift for free draw)' })
        break
      case REMOVE:
        showSnackbar({ message: 'Click on item(s) to remove.' })
        break

      default:
    }
  }

  updateState () {
    const source = this.olLayer.getSource()
    const olFeaturesArr = source.getFeatures()
    let fc = PoziUtils.convertOLFeatureArrToTurfFeature(olFeaturesArr)
    if (fc) {
      fc = toWgs84(fc)
    }
    const features = get(fc, 'features', [])
    if (features.length === 0) {
      Model.unset('app.state.annotation')
    } else {
      const fcStr = JSON.stringify(fc)
      const fcZippedB64 = zip(fcStr)

      Model.setState('annotation', null, fcZippedB64)
    }
  }

  setDrawStyleProperties (feature, options) {
    const { colour, label, type } = options

    // Note: the following feature.set calls set styling in geojsonio.js
    if (label) {
      feature.set('label', label, SILENT_UPDATE)
      feature.set('label-color', colour, SILENT_UPDATE)
    }

    feature.set('stroke-color', colour, SILENT_UPDATE)
    feature.set('stroke-opacity', 1, SILENT_UPDATE)
    feature.set('stroke-width', 3, SILENT_UPDATE)

    feature.set('fill-color', colour, SILENT_UPDATE)
    feature.set('fill-opacity', 0.1, SILENT_UPDATE)

    if (type === POINT) {
      feature.set('marker-symbol', 'circle', SILENT_UPDATE)
    }

    if (type === SKETCH_POINT) {
      feature.set('marker-symbol', 'square', SILENT_UPDATE)
      feature.set('marker-size', 5, SILENT_UPDATE)
      feature.set('stroke-color', colour, SILENT_UPDATE)
      feature.unset('label')
    }

    if (type === POINT_LABEL && !label) {
      feature.set('marker-symbol', 'X', SILENT_UPDATE)
      feature.set('marker-size', 10, SILENT_UPDATE)
      feature.set('stroke-color', '#ff0000', SILENT_UPDATE)
    }

    return feature
  }

  getDrawStyle (options) {
    const { type } = options
    const { colour, label } = this.props

    const styleOptions = {
      type,
      colour,
      label
    }

    const feature = new OLFeature()

    switch (type) {
      case POINT:
      case POINT_LABEL:
      case SKETCH_POINT:
        feature.setGeometry(new OLGeomPoint([0, 0]))
        break
      case LINE_STRING:
        feature.setGeometry(new OLGeomLineString([[0, 0], [1, 1]]))
        break
      case POLYGON:
        delete styleOptions.label
        feature.setGeometry(new OLGeomPolygon([[0, 0], [1, 1], [0, 0]]))
        break
      default:
    }

    // Set relevant properties to allow geojsonio.js to style the feature
    this.setDrawStyleProperties(feature, styleOptions)

    // Get the actual OL style from geosjonio.js. We use a fake layerConf
    // to tell geojsonio what the labelProp field is
    const layerPropConf = { styleGeoJSONIO: { labelProp: 'label' } }
    const style = GeoJSONIO().getStyle(layerPropConf, feature)

    return style
  }

  onNewFeature (evt) {
    const { colour, label, showSnackbar, type } = this.props

    const feature = evt.feature

    // console.debug('On new feature', feature)

    // Don't draw text only feature if the label is empty
    if (type === POINT_LABEL && !label) {
      this.removeFeature(feature)
      showSnackbar({ message: 'Enter text first!', type: SNACKBAR_WARNING })
      return
    }

    // Get GeoJSONIO to create a style for us based on the styling
    // properties we are setting
    const options = {
      colour,
      label,
      type
    }
    this.setDrawStyleProperties(feature, options)

    // Get the actual OL style from geosjonio.js. We use a fake layerConf
    // to tell geojsonio what the labelProp field is
    const layerPropConf = { styleGeoJSONIO: { labelProp: 'label' } }
    const style = GeoJSONIO().getStyle(layerPropConf, feature)

    feature.setStyle(style)

    this.updateState()
  }

  onSelect = (evt) => {
    this.removeFeatures(evt.selected)
  }

  onSourceChange (evt) {
    this.updateState()
    HistoryUpdate()
  }

  removeFeature = (feature) => {
    this.olLayer.getSource().removeFeature(feature)
  }

  removeFeatures = (features = []) => {
    for (const feature of features) {
      this.removeFeature(feature)
    }
  }

  render () {
    return null
  }
}

DrawAnnotations.propTypes = {
  classes: PropTypes.object.isRequired,
  map: PropTypes.object.isRequired,
  layer: PropTypes.object,
  colour: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  showSnackbar: PropTypes.func.isRequired
}

function mapStateToProps (state, ownProps) {
  if (isNil(state.annotationInfo)) {
    return { layer: null, type: '', label: '' }
  }
  return {
    layer: state.layers[state.annotationInfo.titleid],
    colour: state.annotationInfo.colour,
    type: state.annotationInfo.type,
    label: (state.annotationInfo.label || '').trim()
  }
}

const mapDispatchToProps = dispatch => {
  return {
    showSnackbar: options => {
      dispatch(showSnackbarAction(options))
    }
  }
}

export default (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(DrawAnnotations)
  )
)
